import { useState, useRef, useEffect } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useTheme as useCustomTheme } from '../../theme/theme-provider';
import { Box, Typography, Slider, IconButton, Stack } from '@mui/material';
import WaveformProgress from './WaveformProgress';
import {
  PauseRounded,
  PlayArrowRounded,
  FastForwardRounded,
  FastRewindRounded,
  VolumeUpRounded,
  VolumeDownRounded
} from '@mui/icons-material';
import Spinner from '../spinner/Spinner';
import Visualizer from './Visualizer';
import { API } from '../../interceptors/axios';

import postImage2 from "../../static/images/rep.jpg";
import replaceImage from "../../static/images/D_logo_web.ico";

import ShareIcon from '@mui/icons-material/Share';
import { 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Snackbar,
  Alert,
  Button,
} from '@mui/material';
import { Facebook as FacebookIcon, ContentCopy } from '@mui/icons-material';
import XIcon from '@mui/icons-material/Close';

const Widget = styled('div')(({ theme }) => ({
  padding: 16,
  borderRadius: 16,
  width: 343,
  maxWidth: '100%',
  margin: 'auto',
  position: 'relative',
  zIndex: 1,
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.78)' : 'rgba(255,255,255,0.88)',
  backdropFilter: 'blur(40px)',
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(242, 211, 153, 0.25)'
    : '1px solid rgba(0,0,0,0.1)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 32px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.03)'
    : '0 4px 16px rgba(0,0,0,0.08)',
  [theme.breakpoints.down('sm')]: {
    padding: '8px 10px',
    borderRadius: 10,
  },
}));

const TinyText = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  opacity: theme.palette.mode === 'light' ? 0.78 : 0.75,
  color: theme.palette.mode === 'light' ? 'rgba(0,0,0,0.78)' : 'inherit',
  fontWeight: 500,
  letterSpacing: 0.2,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.65rem',
  },
}));

const CoverImage = styled('div')({
  width: 100,
  height: 100,
  objectFit: 'cover',
  overflow: 'hidden',
  flexShrink: 0,
  borderRadius: 8,
  backgroundColor: 'rgba(0,0,0,0.08)',
  '& > img': {
    width: '100%',
  },
});


const MusicPlayer = ({
  song,
  isLoading,
  slug,
  songImage,
  songId,
  fullWidth = false,
  placeholderMode = false,
  placeholderPrimary = 'UNDER CONSTRUCTION',
  placeholderSecondary = 'SOON',
  counterPath = '/song-counter/',
  counterThreshold = 15000, // ms of playback before counting a play
  refreshSong,   // optional async fn — called on first play to fetch a fresh stream URL
}) => {
  // Add loading state for server-side loading
  const [serverLoading, setServerLoading] = useState(!placeholderMode);

  const theme = useTheme();
  const { themeMode: customThemeMode } = useCustomTheme();
  const mainIconColor = customThemeMode === 'dark' ? '#fff' : '#000';
  const volumeIconColor = customThemeMode === 'dark' ? 'rgba(255,255,255,0.82)' : 'rgba(0,0,0,0.56)';

  const audioRef = useRef(null);

  // Core state
  const [isPlaying, setIsPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(30);
  const [localLoading, setLocalLoading] = useState(!placeholderMode);

  // Audio visualization state
  const [audioContext, setAudioContext] = useState(null);
  const [analyzerData, setAnalyzerData] = useState(null);
  const gainNodeRef = useRef(null);
  const audioContextRef = useRef(null); // ref so cleanup always has the latest context
  const sourceNodeRef = useRef(null);  // createMediaElementSource can only be called once per element
  const userInitiatedPauseRef = useRef(false); // true only when the user explicitly clicked pause
  const wantsToPlayRef = useRef(false); // true when user clicked play but play() was aborted (buffering)

  // Add play counter state
  const [hasCountedPlay, setHasCountedPlay] = useState(false);
  const [playStartTime, setPlayStartTime] = useState(null);

  // Buffering state — true while audio is waiting for data after play is clicked
  const [buffering, setBuffering] = useState(false);


  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const getFiniteTime = (value) => (Number.isFinite(value) && value >= 0 ? value : 0);

  const getMediaDuration = (mediaElement) => {
    const directDuration = mediaElement?.duration;
    if (Number.isFinite(directDuration) && directDuration > 0) {
      return directDuration;
    }

    const seekable = mediaElement?.seekable;
    if (seekable && seekable.length > 0) {
      const seekableEnd = seekable.end(seekable.length - 1);
      if (Number.isFinite(seekableEnd) && seekableEnd > 0) {
        return seekableEnd;
      }
    }

    return 0;
  };

  const syncMediaDuration = (mediaElement) => {
    const nextDuration = getMediaDuration(mediaElement);
    setDuration(nextDuration);
    setPosition((currentPosition) => Math.min(currentPosition, nextDuration || currentPosition));
  };

  // Add share functionality
  const handleShareClick = () => {
    setShareDialogOpen(true);
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => {
        setSnackbarMessage('Link copied to clipboard!');
        setSnackbarOpen(true);
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
        setSnackbarMessage('Failed to copy link');
        setSnackbarOpen(true);
      });
  };

  const shareViaFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`;
    window.open(url, '_blank', 'width=600,height=400');
    handleCloseShareDialog();
  };

  const shareViaX = () => {
    const text = `Check out "${slug.replace(/-/g, ' ')}" on Distorise Music`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(window.location.href)}`;
    window.open(url, '_blank', 'width=600,height=400');
    handleCloseShareDialog();
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };


  // Add play counter function
  const updatePlayCount = async (slug) => {
    if (!slug || hasCountedPlay) return;
    
    try {
      setHasCountedPlay(true); // Set first to prevent race conditions
      const response = await API.get(`${counterPath}${slug}`);
      if (response.status < 200 || response.status >= 300) {
        throw new Error('Play count update failed');
      }
    } catch (error) {
      console.error('Play count error:', error);
      // Don't reset hasCountedPlay - if it fails, we don't want to retry
    }
  };

  const setupAudioContext = async () => {
    try {
      if (audioContext) {
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }
        return;
      }

      if (!audioContext) {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
          audioContextRef.current = ctx;
        const analyzer = ctx.createAnalyser();
        const gainNode = ctx.createGain();
        analyzer.fftSize = 2048;
        gainNode.gain.value = volume / 100;

        if (audioRef.current) {
          // Guard: createMediaElementSource can only ever be called ONCE per audio element
          if (!sourceNodeRef.current) {
            const source = ctx.createMediaElementSource(audioRef.current);
            sourceNodeRef.current = source;
          }
          sourceNodeRef.current.connect(gainNode);
          gainNode.connect(analyzer);
          analyzer.connect(ctx.destination);

          audioRef.current.volume = 1;
          gainNodeRef.current = gainNode;

          setAnalyzerData({
            analyzer,
            bufferLength: analyzer.frequencyBinCount,
            dataArray: new Uint8Array(analyzer.frequencyBinCount)
          });
        }
        setAudioContext(ctx);
      }
    } catch (error) {
      console.error('Audio setup failed:', error);
    }
  };

  const applyPlaybackVolume = (nextVolume) => {
    const clamped = Math.max(0, Math.min(100, nextVolume));
    const normalizedVolume = clamped / 100;

    if (gainNodeRef.current && audioContext) {
      const now = audioContext.currentTime;
      gainNodeRef.current.gain.cancelScheduledValues(now);
      gainNodeRef.current.gain.setTargetAtTime(normalizedVolume, now, 0.01);
      if (audioRef.current) {
        audioRef.current.volume = 1;
      }
      return;
    }

    if (audioRef.current) {
      audioRef.current.volume = normalizedVolume;
    }
  };

  const claimMediaSession = (playing) => {
    if (!('mediaSession' in navigator)) return;
    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: slug ? slug.replace(/-/g, ' ') : 'Distorise',
        artist: 'Distorise',
      });
      navigator.mediaSession.playbackState = playing ? 'playing' : 'paused';
      navigator.mediaSession.setActionHandler('play', () => { audioRef.current?.play(); setIsPlaying(true); });
      navigator.mediaSession.setActionHandler('pause', () => { audioRef.current?.pause(); setIsPlaying(false); });
    } catch (_) {}
  };

  const play = async () => {
    try {
      if (placeholderMode) return;
      if (!audioRef.current || localLoading) return;

      if (!isPlaying) {
        // On first play (position 0, never played) refresh the stream URL so tokens are always fresh
        if (refreshSong && audioRef.current.currentTime === 0) {
          try {
            const fresh = await refreshSong();
            if (fresh?.url && fresh.url !== audioRef.current.src) {
              audioRef.current.src = fresh.url;
            }
          } catch (e) {
            console.warn('refreshSong failed, using existing URL:', e.message);
          }
        }
        wantsToPlayRef.current = true;
        setIsPlaying(true);        // optimistic — show Pause button immediately
        claimMediaSession(true);
        try {
          await audioRef.current.play();
          wantsToPlayRef.current = false; // clear on success so onCanPlay doesn't retry
          await setupAudioContext();
        } catch (error) {
          if (error.name === 'AbortError') return; // onCanPlay will retry
          wantsToPlayRef.current = false;
          setIsPlaying(false);
          console.error('Playback failed:', error);
        }
      } else {
        wantsToPlayRef.current = false;
        userInitiatedPauseRef.current = true;
        audioRef.current.pause();
        setIsPlaying(false);
        claimMediaSession(false);
      }
    } catch (error) {
      // AbortError = browser interrupted play() due to buffering or a rapid src change.
      // The audio will resume on its own via onPlaying — don't reset the play state.
      if (error.name === 'AbortError') return;
      console.error('Playback failed:', error);
      setIsPlaying(false);
    }
  };

  const onTimeUpdate = (e) => {
    const mediaElement = e.currentTarget;
    const currentTime = getFiniteTime(mediaElement.currentTime);
    const nextDuration = getMediaDuration(mediaElement);

    if (nextDuration !== duration) {
      setDuration(nextDuration);
    }
    
    if (currentTime > 0 || nextDuration > 0) {
      setPosition(currentTime);
      
      // Initialize start time when playback begins
      if (currentTime < 1 && !playStartTime) {
        setPlayStartTime(Date.now());
      }
      
      // Only count after 15 real seconds have passed
      if (playStartTime && 
          !hasCountedPlay && 
          slug && 
          Date.now() - playStartTime >= counterThreshold) {
        updatePlayCount(slug);
      }
    }
  };

  const onSliderChange = (value) => {
    if (placeholderMode) return;
    const safeDuration = getMediaDuration(audioRef.current) || getFiniteTime(duration);
    if (audioRef.current && safeDuration > 0) {
      const nextValue = Math.max(0, Math.min(value, safeDuration));
      audioRef.current.currentTime = nextValue;
      setPosition(nextValue);
    }
  };

  const onVolumeChange = (_, value) => {
    if (placeholderMode) return;
    const nextVolume = Array.isArray(value) ? value[0] : value;
    setVolume(nextVolume);
    applyPlaybackVolume(nextVolume);
  };

  const setVolumeInstant = (value) => {
    if (placeholderMode) return;
    const clamped = Math.max(0, Math.min(100, value));
    setVolume(clamped);
    applyPlaybackVolume(clamped);
  };

  const formatTime = (value) => {
    const safeValue = getFiniteTime(value);
    const minute = Math.floor(safeValue / 60);
    const secondLeft = Math.floor(safeValue - minute * 60);
    return `${minute}:${secondLeft < 10 ? `0${secondLeft}` : secondLeft}`.substring(0, 6);
  };

  // Load audio when song changes
  useEffect(() => {
      if (placeholderMode) {
        setLocalLoading(false);
        setServerLoading(false);
        return;
      }

      if (!song?.url) return;

      const loadAudio = () => {
          try {
              if (!audioRef.current) return;
            audioRef.current.preload = "metadata";
              audioRef.current.src = song.url;
              audioRef.current.addEventListener('error', () => {
                  setLocalLoading(false);
                  setServerLoading(false);
              });
              // Enable play button immediately
              setLocalLoading(false);
              setServerLoading(false);
          } catch (error) {
              console.error('Audio loading failed:', error);
              setLocalLoading(false);
              setServerLoading(false);
          }
      };

      loadAudio();

      // Reset play tracking when song changes
      setHasCountedPlay(false);
      setPlayStartTime(null);

      return () => {
          if (audioRef.current) {
              audioRef.current.pause();
              audioRef.current.src = '';
          }
          // Do NOT close the AudioContext here — the source node is permanently
          // bound to this audio element and cannot be reconnected after close.
          // Full teardown happens in the unmount-only effect below.
          setIsPlaying(false);
      };
  }, [song, placeholderMode]);

  // Unmount-only cleanup — runs once when the component is removed from the DOM
  useEffect(() => {
      return () => {
          const ctx = audioContextRef.current;
          if (ctx && ctx.state !== 'closed') {
              ctx.close().catch(() => {});
          }
          audioContextRef.current = null;
          sourceNodeRef.current = null;
          gainNodeRef.current = null;
      };
  }, []);


  return (
    <Box sx={{ width: '100%', overflow: 'visible' }}>
      <audio
        ref={audioRef}
        crossOrigin="anonymous"
        preload="metadata"
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={(e) => syncMediaDuration(e.currentTarget)}
        onLoadedData={(e) => syncMediaDuration(e.currentTarget)}
        onDurationChange={(e) => syncMediaDuration(e.currentTarget)}
        onCanPlay={(e) => {
          syncMediaDuration(e.currentTarget);
          setBuffering(false); // data is ready — clear spinner regardless of play state
          if (wantsToPlayRef.current) {
            wantsToPlayRef.current = false;
            audioRef.current?.play()
              .then(() => setupAudioContext())
              .catch(() => {});
          }
        }}
        onProgress={(e) => syncMediaDuration(e.currentTarget)}
        onWaiting={() => setBuffering(true)}
        onPlaying={() => setBuffering(false)}
        onPause={() => {
          // If we didn't initiate the pause (e.g. YouTube/Google scripts via media session),
          // resume immediately and keep the UI in playing state.
          if (userInitiatedPauseRef.current) {
            userInitiatedPauseRef.current = false; // reset for next time
          } else if (isPlaying) {
            // External pause — resume
            audioRef.current?.play().catch(() => {});
          }
        }}
        onEnded={() => {
          const finalDuration = getMediaDuration(audioRef.current) || getFiniteTime(duration);
          setIsPlaying(false);
          setDuration(finalDuration);
          setPosition(finalDuration);
          setBuffering(false);
        }}
      />
      
      <Widget
        sx={{
          width: fullWidth ? '100%' : { xs: '94%', sm: '84%', md: '74%' },
          maxWidth: fullWidth ? '100%' : 920,
          borderRadius: 0,
          ...(customThemeMode === 'dark' && {
            backgroundColor: 'rgba(0,0,0,0.82)',
            border: '1px solid rgba(242,211,153,0.25)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
          }),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CoverImage>
            <img
              alt={slug ? slug.replace(/-/g, ' ') : "Song cover"}
              src={songImage || replaceImage}
              onError={(e) => { e.target.onerror = null; e.target.src = replaceImage; }}
            />
          </CoverImage>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Visualizer
              data={analyzerData}
              buffering={buffering}
              loading={localLoading || serverLoading}
              isPlaying={isPlaying}
              placeholderMode={placeholderMode}
              placeholderPrimary={placeholderPrimary}
              placeholderSecondary={`> ${placeholderSecondary}`}
              themeMode={theme.palette.mode}
            />
          </Box>

        </Box>

        <Box sx={{ width: '100%', maxWidth: '100%', mx: 'auto' }}>
          <WaveformProgress
            songId={songId || '0'}
            position={position}
            duration={duration}
            onSeek={onSliderChange}
            themeMode={customThemeMode}
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <TinyText sx={{ color: customThemeMode === 'dark' ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.72)' }}>{formatTime(position)}</TinyText>
          <TinyText sx={{ color: customThemeMode === 'dark' ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.72)' }}>-{formatTime(Math.max(duration - position, 0))}</TinyText>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: -1 }}>
          <IconButton onClick={() => audioRef.current.currentTime -= 15} disabled={placeholderMode} size="small" sx={{ p: { xs: 0.5, sm: 1 } }}>
            <FastRewindRounded sx={{ fontSize: { xs: 28, sm: 36 } }} htmlColor={mainIconColor}/>
          </IconButton>
          
          <IconButton
            onClick={play}
            disabled={placeholderMode || localLoading}
            sx={{
              p: { xs: 0.75, sm: 1.25 },
              mx: { xs: 0.5, sm: 1 },
              backgroundColor: '#FF2200',
              color: '#fff',
              '&:hover': { backgroundColor: '#cc1900' },
              '&.Mui-disabled': { backgroundColor: 'rgba(255,34,0,0.22)', color: 'rgba(255,255,255,0.35)' },
            }}
          >
            {localLoading || serverLoading || buffering ? <Spinner /> :
              isPlaying ?
              <PauseRounded sx={{ fontSize: { xs: 32, sm: 40 } }} /> :
              <PlayArrowRounded sx={{ fontSize: { xs: 32, sm: 40 } }} />}
          </IconButton>
          
          <IconButton onClick={() => audioRef.current.currentTime += 15} disabled={placeholderMode} size="small" sx={{ p: { xs: 0.5, sm: 1 } }}>
            <FastForwardRounded sx={{ fontSize: { xs: 28, sm: 36 } }} htmlColor={mainIconColor}/>
          </IconButton>

        </Box>

            {/* Share Dialog */}
            <Dialog 
              open={shareDialogOpen} 
              onClose={handleCloseShareDialog}
              PaperProps={{
                sx: {
                  borderRadius: '12px',
                  backgroundColor: 'rgba(30, 30, 30, 0.95)',
                  color: 'white',
                  backdropFilter: 'blur(5px)',
                  maxWidth: '400px',
                  width: '90%'
                }
              }}
            >
              <DialogTitle sx={{ fontFamily: 'Light-1', textAlign: 'center', pt: 3 }}>
                Share "{slug.replace(/-/g, ' ')}"
              </DialogTitle>
              <DialogContent>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, my: 2 }}>
                  <Tooltip title="Share on Facebook">
                    <IconButton 
                      onClick={shareViaFacebook}
                      sx={{ 
                        backgroundColor: '#3b5998', 
                        color: 'white',
                        '&:hover': { backgroundColor: '#4c70ba' }
                      }}
                    >
                      <FacebookIcon />
                    </IconButton>
                  </Tooltip>
                    <Tooltip title="Share on X">
                      <IconButton 
                        onClick={shareViaX}
                        sx={{ 
                          backgroundColor: '#000000', // X uses black branding
                          color: 'white',
                          '&:hover': { backgroundColor: '#333333' }
                        }}
                      >
                        {/* Custom X icon styling for better appearance */}
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative'
                          }}
                        >
                          <XIcon 
                            sx={{ 
                              fontSize: '1.2rem',
                              transform: 'scale(1.2)',
                              fontWeight: 'bold'
                            }} 
                          />
                        </Box>
                      </IconButton>
                    </Tooltip>
                  <Tooltip title="Copy Link">
                    <IconButton 
                      onClick={copyToClipboard}
                      sx={{ 
                        backgroundColor: '#6c757d', 
                        color: 'white',
                        '&:hover': { backgroundColor: '#5a6268' }
                      }}
                    >
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                </Box>
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                <Button 
                  onClick={handleCloseShareDialog}
                  sx={{ 
                    color: '#F2D399',
                    fontFamily: 'Light',
                    '&:hover': { backgroundColor: 'rgba(242, 211, 153, 0.1)' }
                  }}
                >
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
              open={snackbarOpen}
              autoHideDuration={3000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert 
                onClose={handleCloseSnackbar} 
                severity="success" 
                sx={{ 
                  width: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                  color: '#F2D399',
                  '& .MuiAlert-icon': {
                    color: '#F2D399'
                  }
                }}
              >
                {snackbarMessage}
              </Alert>
            </Snackbar>

        <Stack spacing={2} direction="row" sx={{ mb: 1, px: { xs: 0.5, sm: 1 } }} alignItems="center">
          <IconButton
            size="small"
            onClick={() => setVolumeInstant(0)}
            disabled={placeholderMode}
            aria-label="Mute volume"
            sx={{ p: 0.5 }}
          >
            <VolumeDownRounded htmlColor={volumeIconColor} />
          </IconButton>
          <Slider
            aria-label="Volume"
            size="small"
            value={volume}
            onChange={onVolumeChange}
            disabled={placeholderMode}
            sx={{ color: customThemeMode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.87)' }}
          />
          <IconButton
            size="small"
            onClick={() => setVolumeInstant(100)}
            disabled={placeholderMode}
            aria-label="Set maximum volume"
            sx={{ p: 0.5 }}
          >
            <VolumeUpRounded htmlColor={volumeIconColor} />
          </IconButton>
        </Stack>


          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            width: '100%',
            mt: 2,
            mb: 2,
            position: 'relative'
          }}>
            {/* Horizontal line */}
            <Box 
              sx={{
                position: 'absolute',
                width: '80%',
                height: '1px',
                backgroundColor: customThemeMode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)',
                top: '50%',
                left: '10%',
                zIndex: 0
              }}
            />
            
            {/* Share button with solid black background */}
            <Tooltip 
              title="Share"
              placement="top"
              arrow
              sx={{
                '& .MuiTooltip-tooltip': {
                  backgroundColor: '#000000', // Solid black tooltip
                  color: 'white',
                  fontSize: '0.8rem',
                  padding: '6px 10px',
                  marginBottom: '4px'
                }
              }}
            >
              <span>
                <IconButton 
                  onClick={handleShareClick}
                  disabled={placeholderMode}
                  sx={{ 
                    backgroundColor: '#000000', // Solid black background regardless of theme
                    color: 'white', // White icon for contrast
                    '&:hover': { 
                      backgroundColor: '#000000', // Stay black on hover
                      color: '#F2D399' // Gold color on hover
                    },
                    transition: 'all 0.2s ease',
                    padding: '6px',
                    zIndex: 1,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.5)' // Slightly stronger shadow
                  }}
                >
                  <ShareIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

      </Widget>

    </Box>
  );
};

export default MusicPlayer;